'use client'

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslations } from 'next-intl'
import { AlertCircleIcon, ShieldUserIcon, XIcon, PlusCircleIcon, SaveIcon, ShieldIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PermissionSelector, UnsavedChangesGuardDialog } from '@/components/partials'
import { CreateGroupParams, UpdateGroupParams, GroupType } from '@/types/group'
import { usePermissions, useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { FormModeEnum, RoleEnum } from '@/enums'
import { setValidationErrors } from '@/lib/utils'

type PropsType = {
  mode: FormModeEnum
  initialData?: GroupType
  onSubmit: (data: CreateGroupParams | UpdateGroupParams) => void
  onSubmitAndNew?: (data: CreateGroupParams | UpdateGroupParams) => void
  onCancel: () => void
  isLoading?: boolean
}

export type GroupFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

const GroupForm = forwardRef<GroupFormRef, PropsType>(
  ({ mode, initialData, onSubmit, onSubmitAndNew, onCancel, isLoading }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const [selectedRole, setSelectedRole] = useState<string>(initialData?.role || '')

    const form = useForm<CreateGroupParams>({
      defaultValues: {
        role: initialData?.role || '',
        name: initialData?.name || '',
        actionIds: initialData?.permissions ? initialData.permissions.flatMap(p => p.actions.map(a => a.id)) : []
      }
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    const { data: permissions, isLoading: isLoadingPermissions } = usePermissions(
      { roles: selectedRole },
      { enabled: !!selectedRole }
    )

    // Watch role changes
    const watchRole = form.watch('role')
    useEffect(() => {
      if (watchRole !== selectedRole) {
        setSelectedRole(watchRole)
        // Reset action IDs when a role changes
        form.setValue('actionIds', [])
      }
    }, [watchRole, selectedRole, form])

    const handleSubmit = (data: CreateGroupParams) => {
      onSubmit(data)
    }

    const handleSubmitAndNew = (data: CreateGroupParams) => {
      onSubmitAndNew?.(data)
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldUserIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_user_group') : t('edit_user_group')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  rules={{
                    required: t('required')
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('name')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t('name')} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  rules={{
                    required: t('required')
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('role')}</FormLabel>
                      <div className="relative">
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('select')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={RoleEnum.ADMIN}>
                              <div className="flex items-center">
                                <ShieldIcon className="mr-2 h-4 w-4" />
                                {t('admin')}
                              </div>
                            </SelectItem>
                            <SelectItem value={RoleEnum.USER}>
                              <div className="flex items-center">
                                <ShieldIcon className="mr-2 h-4 w-4" />
                                {t('user')}
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {field.value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-0 right-8 h-full px-2 hover:bg-transparent"
                            onClick={() => {
                              field.onChange('')
                              setSelectedRole('')
                            }}>
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Label>{t('permissions')}</Label>
              {!selectedRole ? (
                <Alert>
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertDescription>{t('no_options_available')}</AlertDescription>
                </Alert>
              ) : (
                <FormField
                  control={form.control}
                  name="actionIds"
                  render={({ field }) => (
                    <FormItem>
                      <PermissionSelector
                        permissions={permissions}
                        selectedActionIds={field.value}
                        onActionIdsChange={field.onChange}
                        isLoading={isLoadingPermissions}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    {isLoading || form.formState.isSubmitting
                      ? t('saving')
                      : isCreateModeWithSubmitAndNew
                        ? t('save_and_add_new')
                        : t(mode)}
                  </Button>
                </div>
              </div>
              <UnsavedChangesGuardDialog
                open={isNavigationBlocked}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
              />
            </CardContent>
          </Card>
        </form>
      </Form>
    )
  }
)

GroupForm.displayName = 'GroupForm'

export default GroupForm
